// The two menus in the board's header (PipelineView.swift `pipelineMenu` and `ownerMenu`): which pipeline is on the
// board, grouped by record type, and whose stores it shows.

import { ChevronDown, ListFilter, SquareKanban } from 'lucide-react';
import { useMemo } from 'react';
import type { PipelinePayload } from '@/contracts/models';
import { Button, DropdownMenu, type MenuEntry } from '@/ui';
import { sameId } from '@/domain/util';
import { pipelineTitle, type OwnerOption, type PipelineMenuGroup } from './boardModel';

export interface PipelinePickerProps {
  groups: readonly PipelineMenuGroup[];
  current: PipelinePayload;
  onSelect: (pipelineId: string) => void;
}

export function PipelinePicker({ groups, current, onSelect }: PipelinePickerProps) {
  const items = useMemo<MenuEntry[]>(() => {
    const out: MenuEntry[] = [];
    groups.forEach((g, gi) => {
      if (gi > 0) out.push({ type: 'separator', id: `sep-${g.type}` });
      out.push({ type: 'label', id: `label-${g.type}`, label: g.title });
      for (const p of g.pipelines) {
        out.push({
          id: p.id,
          label: pipelineTitle(p),
          textValue: pipelineTitle(p),
          description: p.isDefault ? 'Default' : undefined,
          checked: sameId(p.id, current.id),
          onSelect: () => onSelect(p.id),
        });
      }
    });
    return out;
  }, [groups, current.id, onSelect]);

  return (
    <DropdownMenu
      label="Choose pipeline"
      minWidth={240}
      items={items}
      trigger={
        <Button icon={<SquareKanban />} iconRight={<ChevronDown />} aria-label={`Pipeline: ${pipelineTitle(current)}. Choose pipeline`}>
          <span className="max-w-[16rem] truncate">{pipelineTitle(current)}</span>
        </Button>
      }
    />
  );
}

export interface OwnerFilterMenuProps {
  options: readonly OwnerOption[];
  /** The current filter (any case); null = Everyone. */
  ownerId: string | null;
  /** The button's text ("Everyone", "My stores", a name). */
  label: string;
  onSelect: (ownerId: string | null) => void;
}

export function OwnerFilterMenu({ options, ownerId, label, onSelect }: OwnerFilterMenuProps) {
  const items = useMemo<MenuEntry[]>(() => {
    const out: MenuEntry[] = [];
    let peopleStarted = false;
    for (const o of options) {
      if (o.kind === 'person' && !peopleStarted) {
        peopleStarted = true;
        out.push({ type: 'separator', id: 'sep-people' }, { type: 'label', id: 'label-people', label: 'People' });
      }
      out.push({
        id: o.ownerId ?? 'everyone',
        label: o.label,
        textValue: o.label,
        description: o.inactive ? 'Inactive' : undefined,
        checked: o.ownerId === null ? ownerId === null : sameId(o.ownerId, ownerId),
        onSelect: () => onSelect(o.ownerId),
      });
    }
    return out;
  }, [options, ownerId, onSelect]);

  const filtered = ownerId !== null;
  return (
    <DropdownMenu
      label="Filter by owner"
      minWidth={220}
      items={items}
      trigger={
        <Button
          variant={filtered ? 'primary' : 'secondary'}
          icon={<ListFilter />}
          iconRight={<ChevronDown />}
          aria-label={`Owner: ${label}. Filter by owner`}
        >
          <span className="max-w-[12rem] truncate">{label}</span>
        </Button>
      }
    />
  );
}
